import { Button, Table, Typography } from 'antd';
import dayjs from 'dayjs';

import {
  ASSET_FIELDS,
  ASSET_KEYS,
  EXPENSE_FIELDS,
  ExpenseKey,
} from './constant';
import { FactFindFormType } from './schema';
import { formatAmount, toAmount } from './Steps';

const fmtDate = (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '');
const show = (v: unknown) =>
  v === undefined || v === null || v === '' ? 'None' : String(v);
const has = (v: unknown) => v !== undefined && v !== null && v !== '';

// Order the expenses are listed in on the paper fact-find (and the design).
const EXPENSE_SUMMARY_ORDER: ExpenseKey[] = [
  'residenceCosts',
  'groceries',
  'mediaSubscriptions',
  'transport',
  'clothingPersonalCare',
  'recreationEntertainment',
  'childcare',
  'petCare',
  'medicalHealth',
  'publicEducation',
  'healthInsurance',
  'basicInsurances',
  'childSpousalMaintenance',
  'insuranceLifeAccident',
  'educationFees',
  'privateSchooling',
];

type Row = { field: string; value: string };
type ReviewSection = { step: number; title: string; rows: Row[] };

const row = (field: string, value: unknown): Row => ({
  field,
  value: show(value),
});

const buildReview = (v: FactFindFormType): ReviewSection[] => {
  const cur = v.expenseCurrency || 'AUD';
  const money = (prefix: string, amount: unknown) =>
    has(amount) ? `${prefix} ${formatAmount(toAmount(amount as string))}` : '';

  const expenseRows: Row[] = [row('Currency', cur)];
  EXPENSE_SUMMARY_ORDER.forEach((k) => {
    // Blank shows "None"; only a typed 0 shows "AUD 0".
    expenseRows.push(row(EXPENSE_FIELDS[k].label, money(cur, v[k])));
    if (k === 'medicalHealth' && has(v.medicalHealthZeroReason)) {
      expenseRows.push(
        row(
          'Reason for 0 Medical and Health expenses',
          v.medicalHealthZeroReason,
        ),
      );
    }
  });
  const total = EXPENSE_SUMMARY_ORDER.reduce((s, k) => s + toAmount(v[k]), 0);
  expenseRows.push(
    row(
      'Total monthly household living expenses',
      `${cur} ${formatAmount(total)}`,
    ),
  );

  return [
    {
      step: 1,
      title: 'Personal Details',
      rows: [
        row('Legal First Name', v.firstName),
        row('Legal Middle Name', v.middleName),
        row('Legal Last Name', v.lastName),
      ],
    },
    {
      step: 1,
      title: 'Address History',
      rows: [
        row('Current Address', v.currentAddress?.formatted_address),
        row('Current Address (Start Date)', fmtDate(v.currentAddressStartDate)),
        ...(v.previousAddresses ?? []).flatMap((a, i) => [
          row(`Previous Address ${i + 1}`, a.address?.formatted_address),
          row(`Previous Address ${i + 1} (Start Date)`, fmtDate(a.startDate)),
          row(`Previous Address ${i + 1} (End Date)`, fmtDate(a.endDate)),
        ]),
      ],
    },
    {
      step: 1,
      title: 'Employment History',
      rows: [
        row('Current Occupation', v.currentOccupation),
        row('Current Employer', v.currentEmployer),
        row(
          'Address of Current Employer',
          v.currentEmployerAddress?.formatted_address,
        ),
        row(
          'When did you start this current role?',
          fmtDate(v.currentEmploymentStartDate),
        ),
        ...(v.previousEmployments ?? []).flatMap((e, i) => {
          const p = `Previous Employment ${i + 1} · `;
          return [
            row(`${p}Occupation`, e.occupation),
            row(`${p}Employer`, e.employer),
            row(`${p}Address`, e.address?.formatted_address),
            row(`${p}Start Date`, fmtDate(e.startDate)),
            row(`${p}End Date`, fmtDate(e.endDate)),
          ];
        }),
      ],
    },
    { step: 2, title: 'Monthly Household Living Expenses', rows: expenseRows },
    {
      step: 3,
      title: 'Assets',
      rows: [
        ...ASSET_KEYS.map((k) =>
          row(ASSET_FIELDS[k].label, money('AUD', v[k])),
        ),
        row('Other Assets', v.otherAssets),
      ],
    },
    {
      step: 3,
      title: 'Financial Circumstances',
      rows: [
        row(
          'Do you foresee any significant changes to your financial situation in the near future?',
          v.foreseeFinancialChanges === 'yes'
            ? 'Yes'
            : v.foreseeFinancialChanges === 'no'
              ? 'No'
              : '',
        ),
        ...(v.foreseeFinancialChanges === 'yes'
          ? [row('Detailed answer', v.financialChangesDetail)]
          : []),
        row('At what age do you plan to retire?', v.retirementAge),
        row('Declaration', v.declaration ? 'Accepted' : ''),
      ],
    },
  ];
};

const cardStyle = {
  background: '#ffffff',
  border: '1px solid #e1e1e1',
  borderRadius: 8,
};

const SummaryCard = ({ sec }: { sec: ReviewSection }) => (
  <section style={cardStyle}>
    <h2
      style={{
        margin: 0,
        padding: '18px 24px',
        borderBottom: '1px solid #e1e1e1',
        fontSize: 19,
        fontWeight: 500,
        color: '#1e293b',
      }}
    >
      {sec.title}
    </h2>
    <div style={{ padding: 24 }}>
      <dl
        style={{
          margin: 0,
          display: 'grid',
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
          gap: '24px 32px',
        }}
      >
        {sec.rows.map((r) => (
          <div
            key={r.field}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
          >
            <dt style={{ fontSize: 14, color: '#64748b' }}>{r.field}</dt>
            <dd
              style={{
                margin: 0,
                fontSize: 16,
                fontWeight: 500,
                color: '#1e293b',
                overflowWrap: 'anywhere',
              }}
            >
              {r.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  </section>
);

interface FactFindSectionsProps {
  values: FactFindFormType;
  onEdit?: (step: number) => void;
  variant?: 'table' | 'cards';
}

const FactFindSections = ({
  values,
  onEdit,
  variant = 'table',
}: FactFindSectionsProps) => {
  const sections = buildReview(values);

  if (variant === 'cards') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {sections.map((sec) => (
          <SummaryCard key={sec.title} sec={sec} />
        ))}
      </div>
    );
  }

  return (
    <>
      {sections.map((sec) => (
        <div key={sec.title} style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
            }}
          >
            <Typography.Title level={5} style={{ margin: 0 }}>
              {sec.title}
            </Typography.Title>
            {onEdit && (
              <Button
                type="link"
                aria-label={`Edit ${sec.title}`}
                onClick={() => onEdit(sec.step)}
              >
                Edit
              </Button>
            )}
          </div>
          <Table
            bordered
            size="middle"
            pagination={false}
            rowKey="field"
            dataSource={sec.rows}
            columns={[
              { title: 'Field', dataIndex: 'field', width: '40%' },
              { title: 'Value', dataIndex: 'value' },
            ]}
          />
        </div>
      ))}
    </>
  );
};

export default FactFindSections;
